import { FocusEvent, FunctionComponent, useEffect, useState } from 'react';

import { Stack } from '@mui/material';

import { INoticeLink, NOTICE_LINK_DESCRIPTION_MAX_LENGTH, NOTICE_LINK_URL_MAX_LENGTH } from '@dw/models/notice';
import { getSpacingCss, verifyValidUrl } from '@dw/utils';

import { Button } from './Button';
import { FormTextInput } from './form';
import { IModalPropsBase, Modal } from './Modal';

interface ILinkForm {
  description: string;
  url: string;
}

const initForm = (link?: INoticeLink): ILinkForm => ({
  description: link?.description ?? '',
  url: link?.url ?? ''
});

const HTTPS_PREFIX = 'https://';

/** Non-empty to the browser's `required`, empty to the API's NotEmpty. Only the portal sees this. */
const isBlank = (value: string): boolean => !!value && !value.trim();

/**
 * What a person typed, rewritten into the address that will actually be saved.
 *
 * A scheme is ADDED only when there is no scheme at all — the test is `://` rather than a bare
 * `https`, so `http://x` and `ftp://x` keep the scheme they were given and fall through to the error
 * below. Silently upgrading those would hide a real mistake behind an address the studio never chose.
 *
 * An existing scheme is lower-cased, and nothing else is. The API's pattern is case-sensitive, so
 * `HTTPS://x` is refused server side, and correcting it here can only ever turn a rejection into a
 * save — while the studio watches it happen on blur, exactly as they watch `https://` be added.
 * The rest of the address is left alone: paths and query strings ARE case-sensitive, and folding
 * `?ref=AbC` to lower case would quietly hand families a different link than the one chosen.
 */
const normaliseUrl = (url: string): string => {
  const trimmed = url.trim();
  if (!trimmed) {
    return trimmed;
  }

  const schemeEnd = trimmed.indexOf('://');
  if (schemeEnd < 0) {
    return `${HTTPS_PREFIX}${trimmed}`;
  }

  return trimmed.slice(0, schemeEnd).toLowerCase() + trimmed.slice(schemeEnd);
};

/**
 * The one thing wrong with an address, or undefined when the API will store it.
 *
 * Mirrors the API's rules rather than a friendlier subset of them, via the SAME predicate the API's
 * pattern is written as — `verifyValidUrl`, already used for the dance school's website. Anything a
 * looser gate here lets through fails on the notice's own Save instead: a snackbar naming a link
 * rule, raised after this dialog is gone, with no way to tell which of five links it means.
 *
 * Ordered so the answer names the actual mistake. A wrong scheme and an unstorable character need
 * different things from the person, so they never share a message.
 */
const validateUrl = (raw: string): string | undefined => {
  // Empty is left to `required` — two messages on one empty input is noise.
  if (!raw) {
    return undefined;
  }

  if (isBlank(raw)) {
    return 'Add the address the link should open';
  }

  // Judged on the NORMALISED address, not the raw one. Enter submits a form without firing blur, so
  // testing what was typed would reject a bare `tickets.example.co.nz` that the blur handler would
  // have fixed a moment later.
  const url = normaliseUrl(raw);

  // The scheme is now lower-cased, so this is about which scheme it is, never how it was typed.
  if (!url.startsWith(HTTPS_PREFIX)) {
    return 'Links must start with https://';
  }

  // Before the pattern, which would otherwise blame a bare `https://` on its characters — of which
  // it has none.
  if (url === HTTPS_PREFIX) {
    return 'Add the rest of the address after https://';
  }

  // Measured on the normalised address rather than the raw one the input's maxLength caps, because
  // the blur handler writes `https://` into state itself: a 500-character bare domain arrives 508.
  if (url.length > NOTICE_LINK_URL_MAX_LENGTH) {
    return `Links must be ${NOTICE_LINK_URL_MAX_LENGTH} characters or fewer, counting the https://`;
  }

  if (!verifyValidUrl(url)) {
    return "That address has characters we can't use — try copying it straight from your browser";
  }

  return undefined;
};

interface ILinkEditorModalProps extends IModalPropsBase {
  /** Omit to add a new link. */
  link?: INoticeLink;
  /** The owning list holds the links until the form around it is saved, so this only hands one back. */
  onSave: (link: INoticeLink) => void;
}

/**
 * Add or edit ONE link.
 *
 * A dialog rather than a row of inputs because `FormTextInput` binds by a static `name` to the
 * enclosing form's state — a dynamic list of rows has no such name to bind to. Editing one item at a
 * time in its own small form is how the portal already solves that (`CreateOrEditExamExaminerModal`).
 *
 * Nesting it inside another form modal is safe: MUI `Dialog` renders through a portal, so this
 * `<form>` is never a DOM descendant of the outer one, and core `Form` stops the submit event
 * propagating up the React tree — which portals otherwise do.
 */
export const LinkEditorModal: FunctionComponent<ILinkEditorModalProps> = (props: ILinkEditorModalProps) => {

  const { link, onSave, ...modalBaseProps } = props;

  const isEdit = !!link;

  const [formData, setFormData] = useState<ILinkForm>(initForm(link));

  // Re-seeded on every open: the dialog stays mounted between openings, so without this, editing one
  // link and then another would show the first one's address. `link` is snapshotted by the owning
  // list when the dialog opens, so its identity cannot change mid-edit and wipe the form.
  useEffect(() => {
    if (modalBaseProps.open) {
      setFormData(initForm(link));
    }
  }, [modalBaseProps.open, link]);

  const customErrors: Partial<Record<keyof ILinkForm, string>> = {};

  // Spaces satisfy `required`, so the browser never marks either field invalid and core Form's
  // `:invalid` sweep finds nothing to stop the save. Both trim away to nothing, and a row whose
  // description AND address are both blank is dropped by the API's NormaliseLinks WITHOUT an error —
  // the notice saves and the link is simply gone. Refusing the row here is what closes that.
  if (isBlank(formData.description)) {
    customErrors.description = 'Give the link a description';
  }

  const urlError = validateUrl(formData.url);
  if (urlError) {
    customErrors.url = urlError;
  }

  /**
   * Rewrite the address the moment the person leaves the field, not on save.
   *
   * Someone who pastes `tickets.example.co.nz` sees it become `https://tickets.example.co.nz` while
   * they are still looking at it, so the address they approve is the address that is stored. Read
   * from the event rather than form state so a keystroke that has not landed yet is included.
   */
  const handleUrlBlur = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const normalised = normaliseUrl(e.target.value);
    if (normalised !== e.target.value) {
      setFormData(fd => ({ ...fd, url: normalised }));
    }
  };

  const handleSubmit = (fd: ILinkForm) => {
    onSave({ description: fd.description.trim(), url: normaliseUrl(fd.url) });
  };

  return (
    <Modal<ILinkForm>
      {...modalBaseProps}
      title={isEdit ? 'Edit Link' : 'Add Link'}
      maxWidth='xs'
      // MUI zeroes DialogContent's top padding after a title, which clips the first floating label.
      contentTopPadding={getSpacingCss(2)}
      formWrapper={{
        formData,
        formDataChanged: setFormData,
        onSubmit: handleSubmit,
        customErrors,
        fullWidth: true
      }}
      actions={(
        <>
          <Button variant='text' color='error' type='button' onClick={modalBaseProps.onClose}>
            Cancel
          </Button>
          <Button variant='text' type='submit'>
            {isEdit ? 'Save Link' : 'Add Link'}
          </Button>
        </>
      )}
    >
      <Stack rowGap={2}>
        <FormTextInput<ILinkForm>
          name='description'
          label='Description'
          required
          maxLength={NOTICE_LINK_DESCRIPTION_MAX_LENGTH}
          margin='none'
          helperText="What people will see — e.g. 'Buy concert tickets'"
        />

        {/* Deliberately not `type='url'`: core Form decides validity with a `:invalid` query, and
            native URL validation would reject a bare address on Enter before the blur handler above
            ever got to fix it. `inputMode` still asks for the right soft keyboard. */}
        <FormTextInput<ILinkForm>
          name='url'
          label='Address'
          inputMode='url'
          required
          maxLength={NOTICE_LINK_URL_MAX_LENGTH}
          margin='none'
          onBlur={handleUrlBlur}
          helperText='Paste the address — https:// is added if you leave it off'
        />
      </Stack>
    </Modal>
  );
};
